/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { selfFetcher, selfUpdater } from '@/data/Content/_Person';
import { useSettings } from '@/data/Settings';
import { DATA_KEY_PERSON } from '@/data/constants/dataKey';
import { EditableAddress } from '@/data/types/Address';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { Person, PersonContact } from '@/data/types/Person';
import { personalContactInfoMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personalContactInfoMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { useCallback, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';

export type EditablePersonInfo = Omit<EditableAddress, 'addressType' | 'nickName'> & {
	parentOrgId?: string;
	dateOfBirth?: string;
	lastNameMat?: string;
	gender?: string;
	receiveEmail?: boolean;
};
export type ChangePasswordValues = {
	xcred_logonPasswordOld: string;
	logonPassword: string;
	xcred_logonPasswordVerify: string;
};

const dataMap = (data: Person, storeId: string): EditablePersonInfo => {
	const {
		firstName = '',
		lastName = '',
		email1 = '',
		addressLine = ['', ''],
		city = '',
		country = '',
		state = '',
		zipCode = '',
		phone1 = '',
		orgizationId: parentOrgId,
		gender,
	} = data ?? {};

	const lastNameMat =
		data?.attributes?.find((attr) => attr.contactInfoAttrKey === 'addressField1')
			?.contactInfoAttrValue ?? '';

	const rfc =
		data?.attributes?.find((attr) => attr.contactInfoAttrKey === 'addressField2')
			?.contactInfoAttrValue ?? '';

	const dateOfBirth = data?.dateOfBirth
		? new Date(data.dateOfBirth).toISOString().split('T')[0]
		: '';

	const receiveEmail =
		data?.receiveEmailPreference?.find((preference) => preference.storeID === storeId)?.value ??
		'false';

	return {
		firstName,
		lastName,
		email1,
		addressLine1: addressLine[0] ?? '',
		addressLine2: addressLine[1] ?? '',
		city,
		country,
		state,
		zipCode,
		phone1,
		parentOrgId,
		lastNameMat,
		dateOfBirth,
		gender,
		rfc,
		receiveEmail: receiveEmail === 'true',
	};
};

export const usePersonInfo = () => {
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const { showSuccessMessage, notifyError } = useNotifications();
	const [editing, setEditing] = useState(false);
	const [changePassword, setChangePassword] = useState(false);
	const { on, off, open } = useIsOpen();
	const {
		data,
		error: personInfoError,
		mutate: mutatePersonInfo,
	} = useSWR(
		settings?.storeId
			? [
					{
						storeId: settings.storeId,
					},
					DATA_KEY_PERSON,
			  ]
			: null,
		async ([props]) => selfFetcher(true)(props.storeId, undefined, params)
	);

	const edit = useCallback(() => {
		setEditing(true);
	}, []);

	const cancelEdit = useCallback(() => {
		setEditing(false);
	}, []);

	const savePersonInfo = useCallback(
		async (info: EditablePersonInfo) => {
			const storeId = settings?.storeId ?? '';
			const { receiveEmail, lastNameMat, addressLine1, addressLine2, rfc, ..._address } = info;

			const new_address = Object.entries(_address).reduce<Record<string, unknown>>(
				(acc, [key, value]) => {
					if (!value) return acc;

					return {
						...acc,
						[key]: value,
					};
				},
				{}
			);

			const _addressLine1 = addressLine1 ? addressLine1 : 'Sin dirección';

			const data = {
				addressLine: [_addressLine1, addressLine2 ?? ''],
				country: new_address?.country ?? 'México',
				...new_address,
				attributes: [
					{
						contactInfoAttrValue: lastNameMat,
						contactInfoAttrKey: 'addressField1',
					},
				],
				receiveEmailPreference: [
					{
						storeId,
						value: receiveEmail?.toString() ?? 'false',
					},
				],
			};

			try {
				on();
				await selfUpdater(true)(storeId, undefined, data, params);
				mutate(personalContactInfoMutatorKeyMatcher(''), undefined);
				setEditing(false);
				showSuccessMessage(
					'Los datos de tu información personal se han actualizado correctamente.',
					'Actualización exitosa'
				);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			} finally {
				off();
			}
		},
		[notifyError, settings?.storeId, showSuccessMessage, params, off, on]
	);

	const closePasswordDialog = useCallback(() => {
		setChangePassword(false);
	}, []);

	const openPasswordDialog = useCallback(() => {
		setChangePassword(true);
	}, []);

	const updatePassword = useCallback(
		async (value: ChangePasswordValues) => {
			const data = { resetPassword: 'true', ...value };
			const storeId = settings?.storeId ?? '';
			try {
				await selfUpdater(true)(storeId, undefined, data, params);
				setChangePassword(false);
				showSuccessMessage(
					'El cambio de contraseña se ha realizado correctamente.',
					'Cambio exitoso'
				);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[notifyError, settings?.storeId, showSuccessMessage, params]
	);

	const { personInfo, primaryAddress } = useMemo(() => {
		const storeId = settings?.storeId ?? '';
		const personInfo = dataMap(data || {}, storeId);

		const { nickName, addressLine, addressType, addressId } = data ?? {};
		const primaryAddress = nickName
			? ({
					...personInfo,
					addressId,
					addressLine,
					nickName,
					addressType,
					primary: 'true',
			  } as PersonContact)
			: undefined;
		return { personInfo, primaryAddress };
	}, [data, settings?.storeId]);

	return {
		personInfo,
		primaryAddress,
		mutatePersonInfo,
		personInfoError,
		editing,
		edit,
		cancelEdit,
		savePersonInfo,
		updatePassword,
		closePasswordDialog,
		openPasswordDialog,
		changePassword,
		open,
	};
};
